import { BaseRespone } from 'src/app/models/common.response';
import { CommonModule, DatePipe } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { MaterialModule } from 'src/app/material.module';
import { AbdmHealthRecordsService } from './abdm-health-records.service';
import { TablerIconsModule } from 'angular-tabler-icons';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { MY_FORMATS } from 'src/app/common/dateFormat';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';

export interface AbdmConsentRequestDialogData {
  readonly patientId: number;
  readonly abhaAddress?: string | null;
  readonly abhaNumber?: string | null;
}

const END_OF_DAY_TIME = '23:59';

@Component({
  selector: 'app-abdm-consent-request-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MaterialModule, TablerIconsModule, TranslateModule],
    providers: [
    DatePipe,
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
  ],
  templateUrl: './abdm-consent-request-dialog.component.html'
})
export class AbdmConsentRequestDialogComponent {
  readonly hiTypes = [
    'OPConsultation', 'Prescription', 'DischargeSummary', 'DiagnosticReport',
    'ImmunizationRecord', 'HealthDocumentRecord', 'WellnessRecord'
  ];
  submitting = false;
  readonly form;
  minDate: Date;
  today: Date;
  maxDate: Date;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: AbdmConsentRequestDialogData,
    private dialogRef: MatDialogRef<AbdmConsentRequestDialogComponent>,
    private formBuilder: FormBuilder,
    private service: AbdmHealthRecordsService,
    private toastr: ToastrService,
    private translate: TranslateService
  ) {
    const now = new Date();
    this.today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    this.maxDate = now;
    this.minDate = new Date(1800, 0, 1);
    const from = new Date(now); from.setFullYear(from.getFullYear() - 1);
    this.form = this.formBuilder.group({
      purposeCode: ['CAREMGT', Validators.required],
      purposeText: ['Care Management', Validators.required],
      hiTypes: [this.hiTypes, Validators.required],
      permissionFrom: [from, Validators.required],
      permissionTo: [now, Validators.required],
      dataEraseAt: [this.today, Validators.required],
      dataEraseTime: [END_OF_DAY_TIME, Validators.required]
    });
  }

  submit(): void {
    if (this.form.invalid || this.submitting) {
      return;
    }
    const value = this.form.getRawValue();
    const dataEraseAt = this.combineDateAndTime(value.dataEraseAt!, value.dataEraseTime!);
    if (dataEraseAt.isSameOrBefore(moment())) {
      this.toastr.error(this.translate.instant('ABDM.EXPIRY_IN_PAST'));
      return;
    }
    this.submitting = true;
    this.service.createConsent({
      patientId: this.data.patientId,
      purposeCode: value.purposeCode!,
      purposeText: value.purposeText!,
      hiTypes: value.hiTypes!,
      permissionFrom: moment(value.permissionFrom!).startOf('day').toISOString(),
      permissionTo: this.endOfDayOrNow(value.permissionTo!).toISOString(),
      dataEraseAt: dataEraseAt.toISOString()
    }).subscribe({
      next: response => {
        this.toastr.success(response.message || this.translate.instant('ABDM.CONSENT_CREATED'));
        this.dialogRef.close(true);
      },
      error: (error: HttpErrorResponse) => {
        this.submitting = false;
        this.toastr.error(this.resolveError(error.error));
      }
    });
  }

  // The API puts the reason (for example an invalid ABHA address) in errors, not in message.
  private resolveError(body: BaseRespone<unknown> | null): string {
    if (body && body.errors && body.errors.length > 0) {
      return body.errors.join('\n');
    }

    if (body && body.message) {
      return body.message;
    }

    return this.translate.instant('ABDM.CONSENT_CREATE_FAILED');
  }

  // The picker hands back 00:00 of the chosen day, but the doctor means the whole day.
  // The API refuses a future moment, so a To day of today ends at the current time.
  private endOfDayOrNow(date: Date): moment.Moment {
    return moment.min(moment(date).endOf('day'), moment());
  }

  // The time lets a consent expire within minutes, which the NHA expiry test needs.
  private combineDateAndTime(date: Date, time: string): moment.Moment {
    const [hours, minutes] = time.split(':').map(Number);
    return moment(date).startOf('day').add(hours, 'hours').add(minutes, 'minutes');
  }
}
